const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;

export function normalizeComparableText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replaceAll('_', ' ')
    .toLowerCase()
    .trim();
}

export function isClosedLikeStatus(value) {
  const normalized = normalizeComparableText(value);
  return normalized.includes('conclu') || normalized.includes('cancel') || normalized.includes('encerr') || normalized.includes('finaliz');
}

export function isClosedLikeStage(value) {
  const normalized = normalizeComparableText(value);
  return normalized.includes('conclu') || normalized.includes('encerr') || normalized.includes('finaliz');
}

function resolveDueAt(dateValue) {
  if (!dateValue) return null;

  const rawValue = String(dateValue);
  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
    date.setHours(23, 59, 59, 999);
  }

  return date.getTime();
}

export function formatIdleDuration(hours) {
  if (!Number.isFinite(hours) || hours <= 0) return 'agora';
  if (hours < 1) return '< 1h';
  if (hours < 24) return `${Math.floor(hours)}h`;

  const days = Math.floor(hours / 24);
  const remainingHours = Math.floor(hours % 24);
  return remainingHours ? `${days}d ${remainingHours}h` : `${days}d`;
}

export function getTaskFocusSignals(task, currentUserId = '', focusPreferences = null) {
  const now = Date.now();
  const closed = isClosedLikeStatus(task?.status);
  const lastMovementAt = task?.lastMovementAt || task?.updatedAt || task?.createdAt || null;
  const lastMovementTs = lastMovementAt ? new Date(lastMovementAt).getTime() : now;
  const idleHours = Math.max((now - lastMovementTs) / HOUR_IN_MS, 0);
  const dueAt = resolveDueAt(task?.dueDate);
  const overdue = !closed && !!dueAt && dueAt < now;
  const dueSoon = !closed && !!dueAt && dueAt >= now && (dueAt - now) <= DAY_IN_MS;
  const needsAssignee = !closed && !task?.assignee?.id;
  const priorityNormalized = normalizeComparableText(task?.priority);
  const isUrgent = priorityNormalized.includes('urgente');
  const isHighPriority = isUrgent || priorityNormalized.includes('alta');
  const isMine = !!currentUserId && task?.assignee?.id === currentUserId;

  let stalenessLevel = 0;
  const staleLevel1 = Number(focusPreferences?.staleHours?.level1) || 12;
  const staleLevel2 = Number(focusPreferences?.staleHours?.level2) || 24;
  const staleLevel3 = Number(focusPreferences?.staleHours?.level3) || 36;

  if (!closed) {
    if (idleHours >= staleLevel3) stalenessLevel = 3;
    else if (idleHours >= staleLevel2) stalenessLevel = 2;
    else if (idleHours >= staleLevel1) stalenessLevel = 1;
  }

  const attentionScore =
    (overdue ? 240 : 0) +
    (needsAssignee ? 110 : 0) +
    (isUrgent ? 90 : isHighPriority ? 45 : 0) +
    (dueSoon ? 35 : 0) +
    (stalenessLevel * 55) +
    (isMine ? 20 : 0);

  return {
    closed,
    idleHours,
    staleLabel: stalenessLevel ? `Parada há ${formatIdleDuration(idleHours)}` : '',
    stalenessLevel,
    overdue,
    dueSoon,
    needsAssignee,
    isUrgent,
    isHighPriority,
    isMine,
    attentionScore
  };
}

export function compareFocusTasks(a, b, currentUserId = '', focusPreferences = null) {
  const focusA = getTaskFocusSignals(a, currentUserId, focusPreferences);
  const focusB = getTaskFocusSignals(b, currentUserId, focusPreferences);

  if (focusA.attentionScore !== focusB.attentionScore) {
    return focusB.attentionScore - focusA.attentionScore;
  }

  const movementA = new Date(a?.lastMovementAt || a?.updatedAt || a?.createdAt || 0).getTime();
  const movementB = new Date(b?.lastMovementAt || b?.updatedAt || b?.createdAt || 0).getTime();
  if (movementA !== movementB) {
    return movementA - movementB;
  }

  return String(a?.title || '').localeCompare(String(b?.title || ''), 'pt-BR');
}
